import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, ProtectedRoute, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/layouts/PublicLayout";
import ConsumerLayout from "@/layouts/ConsumerLayout";
import AdminLayout from "@/layouts/AdminLayout";

// Public
import Landing from "@/pages/public/Landing";
import HowItWorks from "@/pages/public/HowItWorks";
import ForMerchants from "@/pages/public/ForMerchants";
import FAQ from "@/pages/public/FAQ";
import PublicCategories from "@/pages/public/PublicCategories";
import Login from "@/pages/public/Login";
import Signup from "@/pages/public/Signup";
import Onboarding from "@/pages/public/Onboarding";

// Consumer
import Dashboard from "@/pages/consumer/Dashboard";
import SmartFeed from "@/pages/consumer/SmartFeed";
import Ria from "@/pages/consumer/Ria";
import Search from "@/pages/consumer/Search";
import Categories from "@/pages/consumer/Categories";
import CategoryDetail from "@/pages/consumer/CategoryDetail";
import DealDetail from "@/pages/consumer/DealDetail";
import Wallet from "@/pages/consumer/Wallet";
import Referrals from "@/pages/consumer/Referrals";
import FamilyVault from "@/pages/consumer/FamilyVault";
import Subscriptions from "@/pages/consumer/Subscriptions";
import Travel from "@/pages/consumer/Travel";
import CreditCards from "@/pages/consumer/CreditCards";
import Goals from "@/pages/consumer/Goals";
import Alerts from "@/pages/consumer/Alerts";
import Receipts from "@/pages/consumer/Receipts";
import Profile from "@/pages/consumer/Profile";
import Premium from "@/pages/consumer/Premium";
import BillingSuccess, { BillingCancel } from "@/pages/consumer/Billing";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import MerchantsAdmin from "@/pages/admin/Merchants";
import OffersAdmin from "@/pages/admin/Offers";
import CommissionRulesAdmin from "@/pages/admin/CommissionRules";
import UsersAdmin from "@/pages/admin/Users";
import CashbackApprovals from "@/pages/admin/CashbackApprovals";
import PayoutsAdmin from "@/pages/admin/Payouts";
import FraudAdmin from "@/pages/admin/Fraud";
import AffiliateHunter from "@/pages/admin/AffiliateHunter";
import AffiliateNetworks from "@/pages/admin/AffiliateNetworks";
import Campaigns from "@/pages/admin/Campaigns";
import ReportsAdmin from "@/pages/admin/Reports";

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  if (user.role === "consumer") return <Navigate to={user.onboarded ? "/app" : "/onboarding"} replace />;
  return <Navigate to="/admin" replace />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/for-merchants" element={<ForMerchants />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/categories" element={<PublicCategories />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route
                path="/billing/success"
                element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>}
              />
              <Route
                path="/billing/cancel"
                element={<ProtectedRoute><BillingCancel /></ProtectedRoute>}
              />
            </Route>

            <Route
              path="/onboarding"
              element={
                <ProtectedRoute role="consumer">
                  <Onboarding />
                </ProtectedRoute>
              }
            />

            <Route
              path="/app"
              element={
                <ProtectedRoute role="consumer">
                  <ConsumerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="feed" element={<SmartFeed />} />
              <Route path="ria" element={<Ria />} />
              <Route path="search" element={<Search />} />
              <Route path="categories" element={<Categories />} />
              <Route path="categories/:slug" element={<CategoryDetail />} />
              <Route path="deals/:id" element={<DealDetail />} />
              <Route path="wallet" element={<Wallet />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="family" element={<FamilyVault />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="travel" element={<Travel />} />
              <Route path="credit-cards" element={<CreditCards />} />
              <Route path="goals" element={<Goals />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="receipts" element={<Receipts />} />
              <Route path="profile" element={<Profile />} />
              <Route path="premium" element={<Premium />} />
            </Route>

            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="merchants" element={<MerchantsAdmin />} />
              <Route path="offers" element={<OffersAdmin />} />
              <Route path="commission" element={<CommissionRulesAdmin />} />
              <Route path="users" element={<UsersAdmin />} />
              <Route path="cashback" element={<CashbackApprovals />} />
              <Route path="payouts" element={<PayoutsAdmin />} />
              <Route path="fraud" element={<FraudAdmin />} />
              <Route path="affiliate-hunter" element={<AffiliateHunter />} />
              <Route path="affiliate-networks" element={<AffiliateNetworks />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="reports" element={<ReportsAdmin />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
